/**
 * Pluralize a text by a given number
 *
 * pluralize(1, 'apple') => '1 apple'
 * pluralize(2, 'apple') => '2 apples'
 * pluralize(2, ['mouse', 'mice']) => '2 mice'
 * pluralize(2, 'apple', true) => 'apples'
 *
 * @param number number
 * @param original string|[string, string]  Singular text or an array contains [singular, plural]
 * @param hideNumber boolean? Whether to hide prefix number
 * @return string
 */
export const pluralize = (
    number: number,
    original: string | [string, string],
    hideNumber?: boolean,
): string => {
    const [singular, plural] = Array.isArray(original)
        ? original
        : [original, original + 's'];

    const text = number === 1 ? singular : plural;

    return hideNumber ? text : `${number} ${text}`;
};

export const capitalizeFirstLetter = (string: string): string =>
    typeof string === 'string'
        ? string.charAt(0).toUpperCase() + string.slice(1)
        : string;

/**
 * Clean HTML tags from text and reduce to given size
 *
 * @param text string Text to process
 * @param maxChars number Max chars to trim to
 * @return string
 */
export const cleanTagsFromText = (text: string, maxChars?: number): string => {
    const cleanText = text.replace(/<[^>]*>?/gm, '');
    return cleanText.substring(0, maxChars);
};

/**
 * Decode HTML entities in text content.
 * @param text The text to decode.
 * @returns The decoded text.
 */
export const decodeEntities = (text: string): string => {
    return text
        .toString()
        .replace(/&nbsp;/gi, ' ')
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&quot;/g, '"')
        .replace(/&#0*39;/g, "'")
        .replace(/&#x([0-9a-f]+);/gi, (fullMatch, hex: string) => {
            const codePoint = Number.parseInt(hex, 16);
            try {
                return String.fromCodePoint(codePoint);
            } catch {
                return fullMatch;
            }
        })
        .replace(/&#([0-9]+);/g, (fullMatch, decimal: string) => {
            const codePoint = Number.parseInt(decimal, 10);
            try {
                return String.fromCodePoint(codePoint);
            } catch {
                return fullMatch;
            }
        })
        .replace(/&amp;/g, '&'); // Must replace &amp; last to avoid replacing & in other entities.
};

export const superscriptize = (text: string): string => {
    return ['©', '®', '™', '°'].reduce((currentText, symbolIcon) => {
        return currentText.replace(symbolIcon, `<sup>${symbolIcon}</sup>`);
    }, text);
};

/**
 * Word-boundary truncation without `Intl.Segmenter`: cut at `limit`, then back
 * up to the last space so the result doesn't end mid-word.
 */
const truncateAtLastSpace = (str: string, limit: number): string => {
    const head = str.slice(0, limit);
    // The cut already lands on a word end, so there is no partial word to trim.
    // Backing up regardless would drop a whole trailing word the Segmenter path
    // keeps — `'Hello world x'` at 11 gave `'Hello'` instead of `'Hello world'`.
    if (/\s/.test(str.charAt(limit))) {
        return head.replace(/\s+$/, '');
    }
    const lastSpace = head.lastIndexOf(' ');
    return lastSpace > 0 ? head.slice(0, lastSpace) : head;
};

export const truncateStringAtWord = (
    str: string,
    limit: number,
    locale = 'en-US',
) => {
    if (str.length <= limit) return str;

    // `Intl.Segmenter` only shipped in Firefox 125 (and Chrome 87). This runs
    // inside route `meta()`, which React Router executes in the browser on every
    // client-side navigation, so an unguarded `new Intl.Segmenter` doesn't
    // degrade a meta description — it throws and takes the whole navigation to
    // the error page. SSR never sees it, which is why it only bites on
    // click-through. Feature-detect and fall back.
    if (typeof Intl.Segmenter !== 'function') {
        return truncateAtLastSpace(str, limit);
    }

    const segmenter = new Intl.Segmenter(locale, { granularity: 'word' });
    let lastWordBreak = limit;

    for (let word of segmenter.segment(str)) {
        if (word.index > limit) break;

        // `word.index > 0` keeps a leading whitespace segment from being chosen
        // as the cut point. Without it `lastWordBreak` becomes 0 and the whole
        // string is sliced away — ` foo` truncated to 3 returned an empty
        // string, where the fallback below returns ` fo`.
        if (
            !word.isWordLike &&
            word.index > 0 &&
            word.index - 1 !== lastWordBreak
        ) {
            lastWordBreak = word.index;
        }
    }

    return str.slice(0, lastWordBreak);
};
